"use client";

import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@/shared/ui/icons';

import { splitNativeProps, type NativeMetadataProps } from "./apple-native";
import { uiCursor } from "./cursor";
import { cn } from "./utils";

const RadixSelect = SelectPrimitive as any;
const hasRadixSelect =
  typeof (RadixSelect as any)?.Root !== "undefined" &&
  typeof (RadixSelect as any)?.Trigger !== "undefined" &&
  typeof (RadixSelect as any)?.Content !== "undefined" &&
  typeof (RadixSelect as any)?.Item !== "undefined";

let hasWarnedMissingRadixSelect = false;
const warnMissingRadixSelect = () => {
  if (hasWarnedMissingRadixSelect) return;
  hasWarnedMissingRadixSelect = true;
  console.warn(
    "[ui/select] Radix Select primitives unavailable. Falling back to basic markup.",
  );
};

// Robust helper to filter out ANY generated debug props
const filterGeneratedProps = (props: any) => {
  const clean: any = {};
  if (!props) return clean;
  for (const key in props) {
    // Filter out all generated metadata props to prevent crashes
    if (
      !key.startsWith("_fg") && 
      !key.startsWith("figma-") && 
      !key.startsWith("node-") && 
      key !== "figma_selection_id"
    ) {
      clean[key] = props[key];
    }
  }
  return clean;
};

const SafeIcon = ({ icon: Icon, ...props }: { icon: any, [key: string]: any }) => {
  const clean = filterGeneratedProps(props);
  return <Icon {...clean} />;
};

function Select({
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixSelect) {
    warnMissingRadixSelect();
    const {
      value,
      defaultValue,
      onValueChange,
      open,
      onOpenChange,
      name,
      disabled,
      required,
      ...rest
    } = cleanProps as any;
    return (
      <div data-slot="select-fallback" {...rest}>
        {children}
      </div>
    );
  }
  return (
    <RadixSelect.Root data-slot="select" {...cleanProps}>
      {children}
    </RadixSelect.Root>
  );
}

function SelectGroup({
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixSelect || typeof (RadixSelect as any)?.Group === "undefined") {
    warnMissingRadixSelect();
    const { label, ...rest } = cleanProps as any;
    return (
      <div data-slot="select-group-fallback" {...rest}>
        {children}
      </div>
    );
  }
  return (
    <RadixSelect.Group data-slot="select-group" {...cleanProps}>
      {children}
    </RadixSelect.Group>
  );
}

function SelectValue({
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixSelect || typeof (RadixSelect as any)?.Value === "undefined") {
    warnMissingRadixSelect();
    const { placeholder, ...rest } = cleanProps as any;
    return (
      <span data-slot="select-value-fallback" {...rest}>
        {children ?? placeholder ?? ""}
      </span>
    );
  }
  return (
    <RadixSelect.Value data-slot="select-value" {...cleanProps}>
      {children}
    </RadixSelect.Value>
  );
}

function SelectTrigger({
  className,
  size = "default",
  surface = "default",
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: "sm" | "default";
  surface?: "default" | "muted";
} & NativeMetadataProps) {
  const cleanProps = filterGeneratedProps(props);
  const { nativeProps, rest } = splitNativeProps(cleanProps);
  if (!hasRadixSelect || typeof (RadixSelect as any)?.Trigger === "undefined") {
    warnMissingRadixSelect();
    const { disabled, ...rest } = cleanProps as any;
    return (
      <div
        data-slot="select-trigger-fallback"
        data-native-role={nativeProps["data-native-role"] ?? "select"}
        data-size={size}
        className={cn(
          `border-input data-[placeholder]:text-muted-foreground flex w-full items-center justify-between gap-2 rounded-[var(--ui-control-radius)] border bg-input-background px-3 py-2 text-sm whitespace-nowrap ${uiCursor.interactive}`,
          className,
        )}
        {...nativeProps}
        {...rest}
      >
        {children}
      </div>
    );
  }
  return (
    <RadixSelect.Trigger
      data-slot="select-trigger"
      data-native-role={nativeProps["data-native-role"] ?? "select"}
      data-size={size}
        className={cn(
          `border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-muted-foreground aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-full items-center justify-between gap-2 rounded-[var(--ui-control-radius)] border bg-input-background px-3 py-2 text-sm whitespace-nowrap transition-[color,box-shadow] outline-none focus-visible:ring-0 disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 ${uiCursor.interactive}`,
          surface === "muted" &&
            // The dark: overrides match the light recipe — Select's
            // base classes carry `dark:bg-input/30 dark:hover:bg-input/50`
            // which would otherwise win in dark mode and leave the
            // muted Select trigger looking different from the muted
            // Input next to it. Restating the muted bg under `dark:`
            // pins both modes to the same `var(--muted)`-derived tone.
            "border-[color:color-mix(in_srgb,var(--border)_70%,transparent)] bg-[color:color-mix(in_srgb,var(--muted)_88%,white_12%)] hover:bg-[color:color-mix(in_srgb,var(--muted)_92%,white_8%)] dark:bg-[color:color-mix(in_srgb,var(--muted)_88%,white_12%)] dark:hover:bg-[color:color-mix(in_srgb,var(--muted)_92%,white_8%)] focus-visible:border-[color:color-mix(in_srgb,var(--border)_70%,transparent)] focus-visible:ring-0",
          className,
        )}
      {...nativeProps}
      {...rest}
    >
      {children}
      <RadixSelect.Icon asChild data-slot="select-icon">
        <SafeIcon icon={ChevronDownIcon} className="size-4 opacity-50" />
      </RadixSelect.Icon>
    </RadixSelect.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = "popper",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content> & NativeMetadataProps) {
  const cleanProps = filterGeneratedProps(props);
  const { nativeProps, rest } = splitNativeProps(cleanProps);
  if (!hasRadixSelect || typeof (RadixSelect as any)?.Content === "undefined") {
    warnMissingRadixSelect();
    const { onCloseAutoFocus, onEscapeKeyDown, ...rest } = cleanProps as any;
    return (
      <div
        data-slot="select-content-fallback"
        data-native-role={nativeProps["data-native-role"] ?? "menu"}
        className={cn(
          "bg-popover text-popover-foreground relative z-[260] max-h-60 overflow-y-auto rounded-[var(--ui-menu-radius)] border shadow-[var(--ui-menu-shadow)]",
          className,
        )}
        {...nativeProps}
        {...rest}
      >
        {children}
      </div>
    );
  }
  return (
    <RadixSelect.Portal>
      <RadixSelect.Content
        data-slot="select-content"
        data-native-role={nativeProps["data-native-role"] ?? "menu"}
        className={cn(
          "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-[260] max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-[var(--ui-menu-radius)] border shadow-[var(--ui-menu-shadow)]",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className,
        )}
        position={position}
        {...nativeProps}
        {...rest}
      >
        <SelectScrollUpButton />
        <RadixSelect.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1",
          )}
        >
          {children}
        </RadixSelect.Viewport>
        <SelectScrollDownButton />
      </RadixSelect.Content>
    </RadixSelect.Portal>
  );
}

function SelectLabel({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixSelect || typeof (RadixSelect as any)?.Label === "undefined") {
    warnMissingRadixSelect();
    const { ...rest } = cleanProps as any;
    return (
      <div
        data-slot="select-label-fallback"
        className={cn("text-muted-foreground px-2 py-1.5 text-xs", className)}
        {...rest}
      />
    );
  }
  return (
    <RadixSelect.Label
      data-slot="select-label"
      className={cn("text-muted-foreground px-2 py-1.5 text-xs", className)}
      {...cleanProps}
    >
      {children}
    </RadixSelect.Label>
  );
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixSelect || typeof (RadixSelect as any)?.Item === "undefined") {
    warnMissingRadixSelect();
    const { value, disabled, ...rest } = cleanProps as any;
    return (
      <div
        data-slot="select-item-fallback"
        className={cn(
          `relative flex w-full items-center gap-2 rounded-[var(--ui-menu-item-radius)] py-1.5 pr-8 pl-2 text-sm ${uiCursor.menuItem}`,
          className,
        )}
        {...rest}
      >
        {children}
      </div>
    );
  }
  return (
    <RadixSelect.Item
      data-slot="select-item"
      className={cn(
        `hover:bg-accent data-[highlighted]:bg-accent [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full items-center gap-2 rounded-[var(--ui-menu-item-radius)] py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2 ${uiCursor.menuItem}`,
        className,
      )}
      {...cleanProps}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <RadixSelect.ItemIndicator>
          <SafeIcon icon={CheckIcon} className="size-4" />
        </RadixSelect.ItemIndicator>
      </span>
      <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
    </RadixSelect.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixSelect || typeof (RadixSelect as any)?.Separator === "undefined") {
    warnMissingRadixSelect();
    const { ...rest } = cleanProps as any;
    return (
      <div
        data-slot="select-separator-fallback"
        className={cn("bg-border pointer-events-none -mx-1 my-1 h-px", className)}
        {...rest}
      />
    );
  }
  return (
    <RadixSelect.Separator
      data-slot="select-separator"
      className={cn("bg-border pointer-events-none -mx-1 my-1 h-px", className)}
      {...cleanProps}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixSelect || typeof (RadixSelect as any)?.ScrollUpButton === "undefined") {
    warnMissingRadixSelect();
    const { ...rest } = cleanProps as any;
    return (
      <div
        data-slot="select-scroll-up-fallback"
        className={cn("flex cursor-default items-center justify-center py-1", className)}
        {...rest}
      />
    );
  }
  return (
    <RadixSelect.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        `flex items-center justify-center py-1 ${uiCursor.interactive}`,
        className,
      )}
      {...cleanProps}
    >
      <SafeIcon icon={ChevronUpIcon} className="size-4" />
    </RadixSelect.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixSelect || typeof (RadixSelect as any)?.ScrollDownButton === "undefined") {
    warnMissingRadixSelect();
    const { ...rest } = cleanProps as any;
    return (
      <div
        data-slot="select-scroll-down-fallback"
        className={cn("flex cursor-default items-center justify-center py-1", className)}
        {...rest}
      />
    );
  }
  return (
    <RadixSelect.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        `flex items-center justify-center py-1 ${uiCursor.interactive}`,
        className,
      )}
      {...cleanProps}
    >
      <SafeIcon icon={ChevronDownIcon} className="size-4" />
    </RadixSelect.ScrollDownButton>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
